import { IOffice } from '@interfaces/office.interface';
import { IUser } from '@interfaces/user.interface';
import { isUserInAdminRole } from '@shared/context/auth';
import { referredReportRequest } from '@shared/services/report.service';
import { userOptionRequest } from '@shared/services/user.service';
import { exportJsonToCsv, getStateName, sortStringColumn } from '@shared/utils/functions';
import { Button, DatePicker, message, Select, Table } from 'antd';
import { Content } from 'antd/es/layout/layout';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { FaFileCsv } from 'react-icons/fa6';
import { MdOutlineQueryStats } from 'react-icons/md';

const columns = isUserInAdminRole() ? [
  { title: <span className='text-sm'>Agent Name</span>, width: 150, dataIndex: 'agentname', key: 'agentname', sorter: sortStringColumn('agentname') },
  { title: <span className='text-sm'>Referred Date</span>, width: 80, dataIndex: 'referreddate', key: 'referreddate', render: (date: any) => date ? dayjs(date).format('MM/DD/YYYY') : '-' },
  { title: <span className='text-sm'>Office</span>, width: 180, dataIndex: 'officename', key: 'officename', sorter: sortStringColumn('officename') },
  { title: <span className='text-sm'>State</span>, width: 100, dataIndex: 'state', key: 'state', render: (value: string) => getStateName(value) },
  { title: <span className='text-sm'>Referred By</span>, width: 150, dataIndex: 'referrername', key: 'referrername', sorter: sortStringColumn('referrername') },
] : [
  { title: <span className='text-sm'>Agent Name</span>, width: 150, dataIndex: 'agentname', key: 'agentname', sorter: sortStringColumn('agentname') },
  { title: <span className='text-sm'>Referred Date</span>, width: 80, dataIndex: 'referreddate', key: 'referreddate', render: (date: any) => date ? dayjs(date).format('MM/DD/YYYY') : '-' },
  { title: <span className='text-sm'>Office</span>, width: 180, dataIndex: 'officename', key: 'officename', sorter: sortStringColumn('officename') },
  { title: <span className='text-sm'>State</span>, width: 80, dataIndex: 'state', key: 'state', render: (value: string) => getStateName(value) },
];

const { RangePicker } = DatePicker;

const Referred = () => {
  const [messageApi, contextHolder] = message.useMessage();
  
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [referredDates, setReferredDates] = useState<any[]>();
  const [results, setResults] = useState<any[]>([]);
  const [selectedOffices, setSelectedOffices] = useState<number[]>([]);
  const [offices, setOffices] = useState<IOffice[]>([]);
  const [selectedAgents, setSelectedAgents] = useState<number[]>([]);
  const [agents, setAgents] = useState<IUser[]>([]);

  useMemo(() => {
    const fetchData = async () => {
      const options = await userOptionRequest();
      setOffices(options.response.offices.filter((office: any) => office.id !== 1).map((office: any) => ({ value: office.id, label: office.name })).sort((a: any, b: any) => a.label.localeCompare(b.label)));
      setAgents( options.response.users.map((user: any) => ({ value: user.id, label: user.fullName })).sort((a: any, b: any) => a.label.localeCompare(b.label)));

      setLoading(false);
    };

    fetchData();
  }, []); 

  const _OnRunReport = () => {
    setFetching(true);

    referredReportRequest({ referredDates, agents: selectedAgents, offices: selectedOffices })
      .then((res: any) => {
        setResults(res.response);
        setFetching(false);
      });
  };

  const _OnExportReport = () => {
    if(results.length > 0) {
      const csvData = results.map(({ __typename, agentname, referreddate, ...rest }) => ({ agentname, referreddate: referreddate ? dayjs(referreddate).format('MM/DD/YYYY') : '', ...rest }));
      exportJsonToCsv(csvData, `Agent_Referred_Report_${new Date().toISOString().replace(/[-:.TZ]/g, '')}`, ['Agent Name', 'Referred Date', 'Office', 'State', 'Referred By']);
      messageApi.open({ type: 'success', content: 'Report data exported correctly' });
    } else {
      messageApi.open({ type: 'error', content: 'There are no records to export' });
    }
  };

  return (
    <>
      {contextHolder}
      <Content className='px-4 h-[97%]'>
        <div className='w-full grid grid-cols-12 grid-rows-2 gap-4 mb-4'>
          <div className='col-span-3 flex items-center'>
            <div className='whitespace-nowrap mr-2'>Referred Date: </div>
            <RangePicker
              format='MM/DD/YYYY'
              className='w-full'
              placeholder={['Start Date', 'End Date']} 
              onChange={(dates) => { dates ? setReferredDates([dayjs(dates[0]).startOf('day').toDate(), dayjs(dates[1]).endOf('day').toDate()]) : setReferredDates([]) }}
            />
          </div>
          <div className='col-span-6 flex items-center'>
            <div className='mr-2'>Office: </div>
            <Select
              mode='multiple'
              showSearch
              placeholder='Select an office'
              optionFilterProp='label'
              className='w-full mr-8'
              maxTagCount='responsive'
              loading={loading}
              options={offices}
              value={selectedOffices}
              onChange={setSelectedOffices}
            />
          </div>
          <div className='col-span-3 flex items-center justify-end'>
            <Button className='rounded-xl' onClick={_OnRunReport} icon={<MdOutlineQueryStats size={20} className='mt-1' />}>Run Report</Button>
            <Button className='rounded-xl ml-2' onClick={_OnExportReport} icon={<FaFileCsv size={16} className='mt-1' />}>Export</Button>
          </div>
          <div className='col-span-6 flex items-center'>
            {isUserInAdminRole() && <div className='mr-2 whitespace-nowrap'>Referred By: </div>}
            {isUserInAdminRole() && 
              <Select
                mode='multiple'
                showSearch
                placeholder='Select an agent'
                optionFilterProp='label'
                className='w-full mr-8'
                maxTagCount='responsive'
                loading={loading}
                options={agents}
                value={selectedAgents}
                onChange={setSelectedAgents}
              />
            }
          </div>
          <div className='col-span-3'></div>
        </div>
        <div className='h-[calc(100vh-280px)]'>
          <Table
            columns={columns}
            loading={fetching}
            size='small'
            pagination={{ 
              pageSizeOptions: ['25', '50', '100'], 
              defaultPageSize: 50,
              total: results.length,
              showTotal: (total) => `${total} Results`,
            }}
            scroll={{ x: 'max-content', y: 'calc(100vh - 380px)' }}
            rowClassName={() => 'font-row-sm'}
            dataSource={results}
          />
        </div>
      </Content>
    </>
  );
};

export default Referred;